"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Layers, TrendingUp, Building2, Calculator, ArrowRight, Sparkles, Info as HelpCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const softwareTiers = [{
  id: "beginner",
  title: "Beginner-Friendly Tools",
  priceRange: "Under £50 / month",
  bestFor: "Best for getting started & local sites",
  description: "Guided, step-by-step platforms that simplify keyword suggestions, on-page checklists, and basic rank tracking without overwhelming technical jargon.",
  features: ["Guided monthly SEO action tasks", "Essential keyword rank tracking", "Basic on-page health audits"]
}, {
  id: "professional",
  title: "Professional Tools",
  priceRange: "Roughly £100–£250 / month",
  bestFor: "Best for scaling organic growth",
  description: "Full-suite platforms offering extensive keyword intelligence, competitor content gap analysis, backlink discovery, and deep site health audits.",
  features: ["Extensive competitor gap analysis", "In-depth technical site crawling", "Historical SERP tracking & backlink data"]
}, {
  id: "enterprise",
  title: "Enterprise-Level Software",
  priceRange: "Roughly $300–$449+ / month",
  bestFor: "Best for multi-site & large operations",
  description: "Heavyweight toolsets built for large product catalogues, massive crawl capacities, dedicated API pipelines, and cross-market international tracking.",
  features: ["High-volume daily crawling & log analysis", "Custom API data access & integrations", "Multi-domain and multi-market monitoring"]
}];
export default function Pricing() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="pricingguarantee" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      {/* Background ambient accents */}
      <div className="absolute top-0 right-1/4 -z-10 w-96 h-96 bg-secondary/40 rounded-lg blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 -z-10 w-96 h-96 bg-primary/5 rounded-lg blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-primary/10 text-primary border-primary/20 px-3.5 py-1 text-xs sm:text-sm font-medium rounded-lg mb-4 inline-flex items-center gap-1.5 shadow-xs">
            <Layers className="w-4 h-4 text-primary" />
            Software Tier & Investment Guide
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground mb-6">
            How to Choose the{" "}
            <span className="bg-secondary text-foreground px-2 py-0.5 rounded-md inline-block my-1 border border-secondary">
              Right DIY SEO Software
            </span>
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed font-sans">
            Selecting the right software comes down to your current stage of growth, technical confidence, and available monthly budget. Here is how I categorise the standard tooling tiers on the market today.
          </p>
        </motion.div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Left Column: 3 Tiers (7 cols) */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true
        }} className="lg:col-span-7 flex flex-col justify-between">
            <Card className="bg-card border-border shadow-md rounded-lg h-full flex flex-col justify-between p-2">
              <CardContent className="p-6 sm:p-8 flex-1 flex flex-col justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 text-primary font-semibold text-xs uppercase tracking-wider mb-2">
                    <Sparkles className="w-4 h-4" />
                    <span>The Three Tool Categories</span>
                  </div>
                  <h3 className="text-2xl font-bold text-foreground font-serif mb-2">
                    Matching Software to Your Operational Scale
                  </h3>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                    You do not need an enterprise software suite when getting started. Pick the tool that matches your current workflow and upgrade only when your site complexity demands deeper diagnostic metrics.
                  </p>

                  <Separator className="my-6" />

                  {/* 3 Tier Cards */}
                  <div className="space-y-4">
                    {softwareTiers.map((tier, i) => <motion.div key={tier.id} data-index={i} variants={itemVariants} className="p-4 sm:p-5 rounded-lg bg-background border border-border/80 hover:border-primary/40 transition-[border-color,box-shadow] shadow-xs">
                        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            {tier.id === "beginner" && <TrendingUp className="w-4 h-4 text-accent" />}
                            {tier.id === "professional" && <Layers className="w-4 h-4 text-primary" />}
                            {tier.id === "enterprise" && <Building2 className="w-4 h-4 text-foreground" />}
                            <h4 className="font-semibold text-base sm:text-lg text-foreground font-sans">
                              {tier.title}
                            </h4>
                          </div>
                          <span className="text-sm font-semibold text-primary font-mono bg-primary/5 px-2.5 py-0.5 rounded-md border border-primary/15 self-start sm:self-auto">
                            {tier.priceRange}
                          </span>
                        </div>

                        <p className="text-xs font-medium text-accent mb-2">
                          {tier.bestFor}
                        </p>

                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-3">
                          {tier.description}
                        </p>

                        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-border/50 text-xs text-foreground/80">
                          {tier.features.map((feat, featIdx) => <li key={featIdx} className="flex items-center gap-1.5">
                              <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                              <span>{feat}</span>
                            </li>)}
                        </ul>
                      </motion.div>)}
                  </div>
                </div>

                <div className="bg-secondary/70 border border-border rounded-lg p-4 sm:p-5 flex items-start gap-3 mt-4">
                  <HelpCircle className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-foreground leading-relaxed">
                    <strong className="text-foreground font-semibold">My Recommendation:</strong> Start with straightforward, focused tools that help you execute consistent weekly optimisations. Buying high-tier software before you have the bandwidth to act on the data often leads to wasted software spend.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column: Cost-Comparison & Consultation CTA (5 cols) */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-5 flex flex-col">
            <Card className="bg-primary text-primary-foreground border-primary shadow-lg rounded-lg h-full flex flex-col justify-between relative overflow-hidden">
              {/* Decorative background glow */}
              <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-primary-foreground/10 rounded-lg blur-2xl pointer-events-none" />

              <CardContent className="p-6 sm:p-8 flex-1 flex flex-col justify-between gap-8 relative z-10">
                <div>
                  <div className="inline-flex items-center gap-2 bg-primary-foreground/15 text-primary-foreground px-3 py-1 rounded-lg text-xs font-semibold uppercase tracking-wider mb-6 border border-primary-foreground/20">
                    <Calculator className="w-3.5 h-3.5" />
                    <span>Cost Reality & ROI</span>
                  </div>

                  <h3 className="text-2xl sm:text-3xl font-bold font-serif mb-3 text-primary-foreground">
                    Is DIY SEO Software Worth It?
                  </h3>
                  <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed mb-6">
                    A clear answer to whether you should manage your search in-house or hire ongoing external retainers.
                  </p>

                  {/* Cost Comparison Box */}
                  <div className="space-y-3 mb-6">
                    <div className="bg-primary-foreground/10 border border-primary-foreground/20 rounded-lg p-4">
                      <div className="text-xs uppercase tracking-wider text-primary-foreground/80 font-semibold mb-1">
                        Full DIY SEO Tool Stack
                      </div>
                      <div className="text-2xl font-extrabold text-primary-foreground font-mono">
                        £400 – £2,000{" "}
                        <span className="text-xs font-normal text-primary-foreground/80 font-sans">
                          / year
                        </span>
                      </div>
                      <p className="text-xs text-primary-foreground/80 mt-1">
                        Covers your keyword tracking, crawler, and content intelligence tools.
                      </p>
                    </div>

                    <div className="bg-primary-foreground/5 border border-primary-foreground/15 rounded-lg p-4">
                      <div className="text-xs uppercase tracking-wider text-primary-foreground/70 font-semibold mb-1">
                        Traditional Agency Retainers
                      </div>
                      <div className="text-xl font-bold text-primary-foreground/90 font-mono">
                        £600 – £1,000+{" "}
                        <span className="text-xs font-normal text-primary-foreground/75 font-sans">
                          / month
                        </span>
                      </div>
                      <p className="text-xs text-primary-foreground/75 mt-1">
                        Equates to roughly £7,200 – £12,000+ per year in recurring agency fees.
                      </p>
                    </div>
                  </div>

                  <p className="text-xs sm:text-sm text-primary-foreground/90 leading-relaxed mb-6">
                    If you have the internal time to execute, DIY software offers immense annual savings. If you need strategic clarity or hands-on technical roadmapping, pairing DIY execution with independent consultant advisory gives you top-tier results without agency bloat.
                  </p>
                </div>

                {/* Final Consultation CTA */}
                <div className="space-y-3">
                  <Button asChild size="lg" className="w-full bg-background text-foreground hover:bg-background/90 font-semibold shadow-md border-0 h-12 text-sm sm:text-base cursor-pointer">
                    <Link to="/ContactPage" className="flex items-center justify-center gap-2 w-full">
                      <span>Book a Free SEO Consultation</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>

                  <p className="text-center text-xs text-primary-foreground/75">
                    Discuss your tool stack & search strategy directly with Yassine Essaadi
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}